/* salarySeries.js — THE ONE salary-series derivation (Build B / C-2).
 *
 * Extracted VERBATIM from PayrollPage.js:49-162 (Calendar-Day proration). Before this, the
 * SAME logic was triple-duplicated: PayrollPage, scripts/migrate-cid3-payroll-actuals.js, and it
 * would have been a 4th copy in the pre-flight — the migrate script's own header asked for exactly
 * this extraction. There is now ONE derivation in the live codebase; PayrollPage, preflightCheck,
 * and the AdminPage onboarding import all IMPORT it. Same rule as matchPayrollRole: exactly one.
 *
 * ★ WHY IT LIVES IN client/src (not server): the codebase's ema pattern is client-computes /
 * server-persists (PayrollPage → buildMonthlyData → POST /actuals/bulk → the server upserts). The
 * server never derives — and cannot: EC2 has no client/src, and stock CRA blocks client imports
 * from outside client/src. So this is a CLIENT module; the onboarding import sends the computed
 * rows and the server persists them, exactly as the PayrollPage edit path already does.
 *
 * PURE: no DB, no I/O. (roster fields, raises, window) → { [YYYY-MM]: amount }.
 */

import { filterActuals, filterForecast } from './dateUtils';
import { isBiweekly, isoDateT, monthEndIso, payPeriod, periodIndexOf, periodsBetween } from './paySchedule';

const r2 = n => Math.round(n * 100) / 100;

// ── Calendar-Day proration ──────────────────────────────────────────────────────────
//   Daily rate = Annual Salary ÷ days in year (365 or 366); proration = daily rate × days worked.
export function daysInMonth(y, m) { return new Date(y, m, 0).getDate(); }
export function daysInYear(y) { return (y % 4 === 0 && (y % 100 !== 0 || y % 400 === 0)) ? 366 : 365; }

// Prorate salary for the hire month (startDate = first day worked).
// Hired in P1 (1st–15th): daily rate × days worked in P1 + full P2 half-month.
// Hired in P2 (16th+):     daily rate × days worked in P2.
export function prorateHire(startDate, monthlySalary) {
  if (!startDate) return monthlySalary;
  const [yr, mo, dy] = startDate.split("-").map(Number);
  const day = dy;
  if (day === 1) return monthlySalary; // starts on the 1st = full month
  const dim = daysInMonth(yr, mo);
  const diy = daysInYear(yr);
  const annualSalary = monthlySalary * 12;
  const dailyRate = annualSalary / diy; // no intermediate rounding
  const halfMonth = r2(monthlySalary / 2);

  if (day <= 15) {
    // Hired in Period 1 — daily rate × days remaining in P1 + full P2
    const p1Days = 15 - day + 1; // start day through 15th
    return r2(r2(dailyRate * p1Days) + halfMonth);
  } else {
    // Hired in Period 2 — daily rate × days remaining in month
    const p2Days = dim - day + 1;
    return r2(dailyRate * p2Days);
  }
}

// Prorate salary for the term month (termDate = last day worked).
// Termed in P1 (1st–15th): daily rate × days worked.
// Termed in P2 (16th+):     full half-month + daily rate × days worked past 15th.
export function prorateTerm(termDate, monthlySalary) {
  if (!termDate) return monthlySalary;
  const [yr, mo, dy] = termDate.split("-").map(Number);
  const day = dy;
  const dim = daysInMonth(yr, mo);
  if (day === dim) return monthlySalary; // last day of month = full month
  const diy = daysInYear(yr);
  const annualSalary = monthlySalary * 12;
  const dailyRate = annualSalary / diy; // no intermediate rounding
  const halfMonth = r2(monthlySalary / 2);

  if (day <= 15) {
    return r2(dailyRate * day);
  } else {
    const p2Days = day - 15;
    return r2(halfMonth + r2(dailyRate * p2Days));
  }
}

// Generate the monthly salary series for one employee, with proration + raises, over `forecastMonths`.
// `forecastMonths` MUST be the FULL model window (historical_start … forecast_through), NOT
// forecast-only: the historical values are never DISPLAYED (is_actuals wins for m ≤ close_month) but
// computePayroll consumes them for calendar-YTD SS/FUTA/SUTA cap accumulation — truncating to the
// forecast silently breaks the tax caps. `raises` = [{ salary, effectiveDate:'YYYY-MM-DD' }].
// `schedule` (optional, 6th): the company's pay schedule (engines/paySchedule.js). Omitted, null, never answered or
// semi-monthly => every line below runs exactly as it always has. Bi-weekly => the bi-weekly arms at the foot of this file.
export function buildMonthlyData(salary, startDate, termDate, raises, forecastMonths, schedule) {
  if (isBiweekly(schedule)) return biweeklyMonthlyData(salary, startDate, termDate, raises, forecastMonths, schedule);
  const md = {};
  if (!startDate) return md;
  const startMk = startDate.slice(0, 7);
  const termMk = termDate ? termDate.slice(0, 7) : null;
  const sortedRaises = (raises || []).filter(r => r.salary && r.effectiveDate).sort((a, b) => a.effectiveDate.localeCompare(b.effectiveDate));

  // Salary for a month considering raises → {salary, isRaiseMonth, prevSalary, raiseDay}
  const getSalaryInfo = m => {
    let s = salary, prevS = salary, isRaise = false, rDay = 1;
    for (const r of sortedRaises) {
      const rMonth = r.effectiveDate.slice(0, 7);
      if (m > rMonth) { s = r.salary; prevS = r.salary; }
      else if (m === rMonth) { prevS = s; s = r.salary; isRaise = true; rDay = parseInt(r.effectiveDate.split("-")[2]) || 1; }
    }
    return { salary: s, prevSalary: prevS, isRaiseMonth: isRaise, raiseDay: rDay };
  };

  (forecastMonths || []).forEach(m => {
    if (m < startMk) return;
    if (termMk && m > termMk) return;
    const info = getSalaryInfo(m);
    let pay;
    if (info.isRaiseMonth && info.raiseDay >= 16) {
      // Raise on the 16th: half at old + half at new
      pay = r2(r2(info.prevSalary / 12 / 2) + r2(info.salary / 12 / 2));
    } else {
      // Raise on the 1st (full month at new) or no raise
      pay = r2(info.salary / 12);
    }
    if (m === startMk && m === termMk) {
      const hireRatio = prorateHire(startDate, 1);
      const termRatio = prorateTerm(termDate, 1);
      md[m] = r2(Math.min(hireRatio, termRatio) * pay);
    } else if (m === startMk) {
      md[m] = prorateHire(startDate, pay);
    } else if (m === termMk) {
      md[m] = prorateTerm(termDate, pay);
    } else {
      md[m] = pay;
    }
  });
  return md;
}

/* regenerateSalarySeries -- THE ONE REGENERATE STEP (W-001, 2026-09-23).
 *
 * buildMonthlyData above is the one DERIVATION. This is the one rule for turning it into the
 * series a caller WRITES after an edit. It has exactly two modes, one per context (Brian,
 * 2026-09-23), and every caller names its mode:
 *
 *   'forward' -- a LIVE-tenant edit (the Payroll Employee Modal, the TermModal). Editing an
 *                existing salary there is a TYPO FIX applied FORWARD from the first forecast
 *                month: FORECAST months are re-derived, CLOSED months keep their stored values
 *                as history. A real pay change is a raise ("+Add Change"), which
 *                buildMonthlyData already applies from its effective date. This is the rule
 *                PayrollPage.js carried inline before this extraction, reproduced exactly:
 *                  { ...closed months present in STORED, ...buildMonthlyData(forecast months) }
 *   'full'    -- a CORRECTION INSIDE THE ONBOARDING WIZARD (Step 5): the number was wrong all
 *                along, so EVERY month of the window is re-derived. Returned ZERO-FILLED over
 *                the whole window, because POST /actuals/bulk is an UPSERT WITH NO DELETE -- a
 *                month the caller does not send keeps its old value, so a sparse map would leave
 *                stale months behind (a terminated month, a pre-start month).
 *
 * NOTE: closed income-statement months come from is_actuals, never from this series. Closed-
 * month series values feed only computePayroll's calendar-YTD SS/FUTA/SUTA cap accumulators, so
 * NEITHER mode can move a closed IS figure.
 *
 * Callers pass the RAW fields: salary (annual), startDate / termDate ('YYYY-MM-DD' or null),
 * raises ([{ salary, effectiveDate }]), allMonths (the FULL model window), closeMonth ('YYYY-MM';
 * 'forward' only), stored ({ [YYYY-MM]: amount }; 'forward' only).
 * Returns { [YYYY-MM]: amount } -- exactly what the caller upserts.
 * `schedule` (optional): the company's pay schedule, handed to buildMonthlyData unchanged. A caller that passes none
 * gets the semi-monthly derivation, as before.
 */
export function regenerateSalarySeries({ mode, salary, startDate, termDate, raises, allMonths, closeMonth, stored, schedule }) {
  const months = allMonths || [];
  if (mode === 'forward') {
    const prior = stored || {};
    const preserved = {};
    filterActuals(months, closeMonth).forEach(m => { if (prior[m] !== undefined) preserved[m] = prior[m]; });
    return { ...preserved, ...buildMonthlyData(salary, startDate, termDate, raises, filterForecast(months, closeMonth), schedule) };
  }
  if (mode === 'full') {
    const derived = buildMonthlyData(salary, startDate, termDate, raises, months, schedule);
    const out = {};
    months.forEach(m => { out[m] = derived[m] || 0; });
    return out;
  }
  throw new Error('regenerateSalarySeries: unknown mode ' + JSON.stringify(mode) + " (expected 'forward' or 'full')");
}

/* ====================================================================================================================
 * THE BI-WEEKLY ARMS (bi-weekly payroll, step 3a, 2026-10-08). Plain ASCII on purpose (CLAUDE.md 12.5 rule 55).
 *
 * Reached ONLY when buildMonthlyData is handed a bi-weekly schedule. Nothing above this line reads anything below it,
 * and the semi-monthly derivation above is untouched (Brian B1: one engine, semi-monthly = the code as it was).
 *
 * THE RULE -- ONE PER-DAY LEDGER (Brian Q1 (b) + A1, 2026-10-08):
 *   the daily rate is annual / 364 (26 pay periods of 14 days), never rounded on its own;
 *   inside each pay period, per PAY-RATE RUN (consecutive worked days at one annual rate), the amount earned through the
 *   run's i-th worked day is r2(i x annual / 364), and a day's amount is the difference of two such figures -- so every
 *   day carries whole cents, fixed once. Everything else is a sum of those days:
 *     a CHECK                  = the days of its pay period (a full period at one rate = r2(annual / 26), exactly)
 *     an ACCRUES month         = the days of the calendar month
 *     a WHEN-PAID month        = the checks DATED in the month
 *     ACCRUED WAGES at a       = the wages EARNED through the month's last day, LESS the checks PAID on or before it
 *       month-end                (SIGNED -- Brian, 2026-10-08, option (b)). Normally that is the days earned and not yet
 *                                paid (>= 0), and a pay period that is finished but not yet paid counts as exactly its
 *                                check. It is NEGATIVE when a holiday moved a check to BEFORE its own period end and the
 *                                month-end falls in between: the days after the month-end that the check has already paid
 *                                are a PREPAID amount (a prepaid day). That needs a lag shorter than the move -- lag 0, or
 *                                lag 1-2 with a Monday holiday.
 *   Because all four are sums of the same day amounts, they cannot disagree about a cent or about a date, and
 *       month salary (accrues) = checks dated in the month + ending accrued wages - beginning accrued wages
 *   holds exactly, in every month -- the signed balance is what makes it hold in the prepaid case too.
 *
 *   a worked day       startDate <= day <= termDate (termDate = the last day worked; none = still employed)
 *   the pay rate on a  the starting salary, or the latest pay change whose effective date is on or before that day -- a
 *     day              pay change takes effect ON its date, ANY date (Brian B3), and splits its pay period there
 *   hire / termination to the DAY. Hired and terminated in one month: the days are simply counted once.
 *   the final check    a leaver's last partial period is paid on that period's normal check date (Brian Q4).
 *
 * ONE PASS -- biweeklySalaryLedger -- fixes every cent; buildMonthlyData (by the company's books answer) and
 * accruedSalaryByMonth both read it. A second implementation of any of these figures is forbidden (Principle 0).
 * Accepted against an oracle that shares no code with this file: server/diagnostics/verify-biweekly-reference.js.
 * ==================================================================================================================== */
const DAY_MS = 86400000;
const BIWEEKLY_DAYS_PER_YEAR = 364;

function dayNumberOf(iso, what) {   // whole days since 1970-01-01 (UTC); a date that is not real is named, never guessed
  const t = isoDateT(iso);
  if (t === null) throw new Error('salarySeries (bi-weekly): ' + what + ' ' + JSON.stringify(iso) + ' is not a real calendar date (YYYY-MM-DD)');
  return t / DAY_MS;
}

// The ledger's one formula: the amount earned through the i-th worked day of a pay-rate run.
// One multiplication, one division, one rounding.
const earnedThrough = (i, annual) => r2((i * annual) / BIWEEKLY_DAYS_PER_YEAR);

// The pay rate over time: [{ from (day number), annual }], ascending. Same filter and order as the semi-monthly arm.
function payRateSteps(salary, raises) {
  const steps = [{ from: -Infinity, annual: Number(salary) || 0 }];
  (raises || []).filter(r => r.salary && r.effectiveDate)
    .sort((a, b) => a.effectiveDate.localeCompare(b.effectiveDate))
    .forEach(r => steps.push({ from: dayNumberOf(r.effectiveDate, 'the pay change date'), annual: Number(r.salary) }));
  return steps;
}

// The pay-rate runs of one pay period: consecutive WORKED days at one annual rate, as [{ from, to, annual }] (day numbers).
function workedRuns(periodStartNo, periodEndNo, firstDayNo, lastDayNo, steps) {
  const runs = [];
  const lo = Math.max(periodStartNo, firstDayNo);
  const hi = Math.min(periodEndNo, lastDayNo);
  if (lo > hi) return runs;
  for (let s = 0; s < steps.length; s++) {
    const from = Math.max(lo, steps[s].from);
    const to = Math.min(hi, s + 1 < steps.length ? steps[s + 1].from - 1 : Infinity);
    if (from > to) continue;
    const prev = runs[runs.length - 1];
    if (prev && prev.annual === steps[s].annual && prev.to + 1 === from) prev.to = to;   // same rate: one run
    else runs.push({ from, to, annual: steps[s].annual });
  }
  return runs;
}

// The ledger amount of the days [a, b] (day numbers) that fall inside the given runs.
function earnedInRange(runs, a, b) {
  let sum = 0;
  for (const run of runs) {
    const lo = Math.max(run.from, a);
    const hi = Math.min(run.to, b);
    if (lo > hi) continue;
    const daysBefore = lo - run.from;        // the run's worked days before the range
    const daysThrough = hi - run.from + 1;   // the run's worked days through the end of the range
    sum = r2(sum + r2(earnedThrough(daysThrough, run.annual) - earnedThrough(daysBefore, run.annual)));
  }
  return sum;
}

/* THE PAY CALENDAR OF THE MONTHS ASKED FOR -- every pay period that overlaps them or is paid in them (each with its check
 * date), and for each month its first / last day. It depends on the schedule and the months
 * only, NOT on the employee, and it is the costly part (each check date is walked off weekends and holidays, measured at
 * about 9 ms for a five-year window). So the last one built is REMEMBERED and reused for the next employee: one entry,
 * keyed by the exact anchor, lag and month list -- the same key always gives the same calendar, so the result of the pass
 * does not depend on it (verify-biweekly-reference asserts a month alone equals the month inside a window, which alternates
 * the key). Nothing else in this file keeps state. */
let rememberedCalendar = null;
function biweeklyCalendar(schedule, list) {
  const key = JSON.stringify([schedule.firstCheckDate, schedule.lagDays, list]);
  if (rememberedCalendar && rememberedCalendar.key === key) return rememberedCalendar;
  const ordered = [...list].sort();
  const periods = periodsBetween(schedule, ordered[0] + '-01', monthEndIso(ordered[ordered.length - 1])).map(p => ({
    ...p,
    startNo: dayNumberOf(p.start, 'a pay period start'),
    endNo: dayNumberOf(p.end, 'a pay period end'),
    checkMonth: p.check.slice(0, 7),
  }));
  const months = list.map(m => {
    const monthEnd = monthEndIso(m);
    return {
      m,
      monthEnd,
      aNo: dayNumberOf(m + '-01', 'a month start'),
      bNo: dayNumberOf(monthEnd, 'a month end'),
    };
  });
  rememberedCalendar = { key, periods, months };
  return rememberedCalendar;
}

/* THE ONE PASS. Returns, for the months asked:
 *   accrues  { 'YYYY-MM': salary }   the days of the month. Keys as the semi-monthly arm: none before the start month,
 *                                    none after the termination month.
 *   whenPaid { 'YYYY-MM': salary }   the checks dated in the month. Its keys run from the start month (or the first
 *                                    check's month, if earlier) through the month of the final check -- which can be
 *                                    the month AFTER the last day worked. A month in between with no check reads 0.
 *   accrued  { 'YYYY-MM': balance }  accrued wages on the month's last day, SIGNED: earned through it less the checks dated
 *                                    on or before it. EVERY month asked carries a key (0 if none). A NEGATIVE balance is
 *                                    a prepaid amount -- days after the month-end that a check has already paid.
 *   checks   [{ date, nominalDate, periodStart, periodEnd, amount }]   every calendar check dated in a month asked, in
 *                                    order, with what it pays this employee (0 when nothing).
 * A month's figures do not depend on which other months are asked for. */
export function biweeklySalaryLedger(salary, startDate, termDate, raises, months, schedule) {
  if (!isBiweekly(schedule)) throw new Error('salarySeries: biweeklySalaryLedger needs a bi-weekly pay schedule');
  const list = months || [];
  const out = { accrues: {}, whenPaid: {}, accrued: {}, checks: [] };
  list.forEach(m => { out.accrued[m] = 0; });
  if (!startDate || !list.length) return out;

  const firstDayNo = dayNumberOf(startDate, 'the start date');
  const lastDayNo = termDate ? dayNumberOf(termDate, 'the last day worked') : Infinity;
  const steps = payRateSteps(salary, raises);

  // every pay period that overlaps the months asked or is paid in them (the calendar -- the same for every employee),
  // each with THIS employee's runs and check amount
  const calendar = biweeklyCalendar(schedule, list);
  const periods = calendar.periods.map(p => {
    const runs = workedRuns(p.startNo, p.endNo, firstDayNo, lastDayNo, steps);
    return { ...p, runs, amount: earnedInRange(runs, p.startNo, p.endNo) };
  });

  const startMk = startDate.slice(0, 7);
  const termMk = termDate ? termDate.slice(0, 7) : null;
  // the months the when-paid series covers: from the earlier of the start month and the first check's month, through the
  // later of the termination month and the final check's month (Q4: the final check keeps its period's normal date)
  const firstCheckMk = payPeriod(schedule, periodIndexOf(schedule, startDate)).check.slice(0, 7);
  const paidFromMk = firstCheckMk < startMk ? firstCheckMk : startMk;
  const finalCheckMk = termDate ? payPeriod(schedule, periodIndexOf(schedule, termDate)).check.slice(0, 7) : null;
  const paidToMk = termMk === null ? null : (finalCheckMk > termMk ? finalCheckMk : termMk);

  for (const { m, monthEnd, aNo, bNo } of calendar.months) {
    // ACCRUES: the days of the month
    if (m >= startMk && !(termMk && m > termMk)) {
      let s = 0;
      for (const p of periods) if (p.endNo >= aNo && p.startNo <= bNo) s = r2(s + earnedInRange(p.runs, aNo, bNo));
      out.accrues[m] = s;
    }

    // WHEN PAID: the checks dated in the month
    if (m >= paidFromMk && !(paidToMk && m > paidToMk)) {
      let s = 0;
      for (const p of periods) if (p.checkMonth === m) s = r2(s + p.amount);
      out.whenPaid[m] = s;
    }

    /* ACCRUED WAGES, SIGNED (Brian, 2026-10-08): the wages earned through the month's last day, less the checks dated on
       or before it. Period by period, from the same day amounts as everything above:
         a period whose check is dated AFTER the month-end is UNPAID  -> + what it has earned through the month-end
                                                                          (a finished period: exactly its check)
         a period whose check is dated ON OR BEFORE it is PAID        -> - what that check paid for days AFTER the month-end
                                                                          (nothing, unless a holiday moved the check to
                                                                          before its own period end: a PREPAID day)
       So the balance is the days earned and unpaid in every ordinary month, and negative only in the prepaid case. */
    let balance = 0;
    for (const p of periods) {
      if (p.check > monthEnd) {
        if (p.startNo <= bNo) balance = r2(balance + earnedInRange(p.runs, p.startNo, bNo));
      } else if (p.endNo > bNo) {
        balance = r2(balance - earnedInRange(p.runs, bNo + 1, p.endNo));
      }
    }
    out.accrued[m] = balance;
  }

  const asked = new Set(list);
  out.checks = periods.filter(p => asked.has(p.checkMonth))
    .map(p => ({ date: p.check, nominalDate: p.nominalCheck, periodStart: p.start, periodEnd: p.end, amount: p.amount }));
  return out;
}

// buildMonthlyData's bi-weekly arm: the month's salary by the company's books answer.
function biweeklyMonthlyData(salary, startDate, termDate, raises, months, schedule) {
  if (schedule.books !== 'accrues' && schedule.books !== 'when_paid')
    throw new Error("salarySeries (bi-weekly): the schedule's books answer must be 'accrues' or 'when_paid' -- got " + JSON.stringify(schedule.books));
  const ledger = biweeklySalaryLedger(salary, startDate, termDate, raises, months, schedule);
  return schedule.books === 'accrues' ? ledger.accrues : ledger.whenPaid;
}

/* ACCRUED WAGES at each month-end, { 'YYYY-MM': balance } -- from the same pass, SIGNED (earned through the month-end less
 * the checks dated on or before it; negative = a prepaid day). Only a bi-weekly company that ACCRUES payroll carries the
 * balance; for any other schedule (none, semi-monthly, bi-weekly expensed when paid) it returns {}.
 * Pass the REAL last day worked as termDate: the day matters here. */
export function accruedSalaryByMonth(salary, startDate, termDate, raises, months, schedule) {
  if (!isBiweekly(schedule) || schedule.books !== 'accrues') return {};
  return biweeklySalaryLedger(salary, startDate, termDate, raises, months, schedule).accrued;
}
